import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function normalizeTimestamp(
  timestamp: string | number | Date,
): string | number | Date {
  if (timestamp instanceof Date) {
    return timestamp;
  }

  if (typeof timestamp === "number") {
    return Math.abs(timestamp) < 1e11 ? timestamp * 1000 : timestamp;
  }

  if (/^-?\d+(\.\d+)?$/.test(timestamp)) {
    const numericTimestamp = Number(timestamp);
    return Math.abs(numericTimestamp) < 1e11
      ? numericTimestamp * 1000
      : numericTimestamp;
  }

  return timestamp;
}

const LOCAL_DATE_TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

const LOCAL_DATE_ONLY_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
};

const LOCAL_TIME_ONLY_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: "numeric",
  minute: "2-digit",
};

const localDateTimeFormatter = new Intl.DateTimeFormat(
  undefined,
  LOCAL_DATE_TIME_FORMAT_OPTIONS,
);

const localDateOnlyFormatter = new Intl.DateTimeFormat(
  undefined,
  LOCAL_DATE_ONLY_FORMAT_OPTIONS,
);

const localTimeOnlyFormatter = new Intl.DateTimeFormat(
  undefined,
  LOCAL_TIME_ONLY_FORMAT_OPTIONS,
);

export function formatLocalDateTime(timestamp: string | number | Date): string {
  const date = new Date(normalizeTimestamp(timestamp));
  return localDateTimeFormatter.format(date);
}

/**
 * The time half alone, in the same locale and options as
 * `formatLocalDateTimeParts().time` — through the same cached formatter, so a
 * caller cannot drift by hand-rolling `toLocaleTimeString` options. For a
 * timestamp the reader knows is recent (the composer's "Saved 3:04 PM"), where
 * the date is noise rather than information.
 */
export function formatLocalTime(timestamp: string | number | Date): string {
  return localTimeOnlyFormatter.format(new Date(normalizeTimestamp(timestamp)));
}

/**
 * Same locale/options as `formatLocalDateTime`, split for stacked display (date line, then time line).
 * Uses separate cached formatters and `.format()` only — avoids `formatToParts` per row.
 */
export function formatLocalDateTimeParts(
  timestamp: string | number | Date,
): { date: string; time: string } {
  const date = new Date(normalizeTimestamp(timestamp));
  return {
    date: localDateOnlyFormatter.format(date),
    time: localTimeOnlyFormatter.format(date),
  };
}
